import axios from 'axios';
import {
    HikvisionError,
    ConnectionError,
    AuthenticationError,
    ApiError,
    TimeoutError,
} from '../errors/HikvisionError';
import { setTimeout } from 'node:timers';

/**
 * Utility class for handling and transforming errors
 */
export class ErrorHandler {
    /**
     * Wraps an async operation with proper error handling
     */
    static async withErrorHandling<T>(
        operation: () => Promise<T>,
        context: string
    ): Promise<T> {
        try {
            return await operation();
        } catch (error) {
            throw ErrorHandler.transformError(error, context);
        }
    }

    /**
     * Transforms various error types into HikvisionError instances
     */
    static transformError(error: unknown, context: string): HikvisionError {
        // If it's already a HikvisionError, return as-is
        if (error instanceof HikvisionError) {
            return error;
        }

        // Handle Axios errors
        if (axios.isAxiosError(error)) {
            return ErrorHandler.handleAxiosError(error, context);
        }

        // Handle timeout errors
        if (error instanceof Error && error.message.includes('timeout')) {
            return new TimeoutError(
                `Operation timed out in ${context}`,
                10000, // Default timeout
                error
            );
        }

        // Handle generic errors
        if (error instanceof Error) {
            return new ConnectionError(
                `Unexpected error in ${context}: ${error.message}`,
                error
            );
        }

        // Handle unknown error types
        return new ConnectionError(
            `Unknown error in ${context}`,
            error instanceof Error ? error : undefined
        );
    }

    private static handleAxiosError(error: any, context: string): HikvisionError {
        const { response, request, message } = error;

        // Network error (no response received)
        if (!response && request) {
            return new ConnectionError(
                `Network error in ${context}: ${message}`,
                error
            );
        }

        // Request setup error
        if (!response && !request) {
            return new ConnectionError(
                `Request setup error in ${context}: ${message}`,
                error
            );
        }

        // HTTP error response
        const statusCode = response?.status;
        const statusText = response?.statusText;

        // Authentication errors
        if (statusCode === 401 || statusCode === 403) {
            return new AuthenticationError(
                `${statusText} in ${context}`,
                error
            );
        }

        // API errors
        return new ApiError(
            `HTTP ${statusCode} ${statusText} in ${context}`,
            statusCode,
            response?.data?.ResponseStatus?.subStatusCode,
            error
        );
    }

    /**
     * Logs error information (can be extended with proper logging library)
     */
    static logError(error: HikvisionError, context?: string): void {
        const logData = {
            timestamp: new Date().toISOString(),
            context,
            error: error.toJSON(),
        };

        // In a real application, you would use a proper logging library
        console.error('Hikvision API Error:', JSON.stringify(logData, null, 2));
    }

    /**
     * Determines if an error is recoverable
     */
    static isRecoverable(error: HikvisionError): boolean {
        switch (error.code) {
            case 'CONNECTION_ERROR':
                return true; // Can retry connection
            case 'AUTHENTICATION_ERROR':
                return false; // Need new credentials
            case 'VALIDATION_ERROR':
                return false; // Need to fix input
            case 'API_ERROR':
                return error instanceof ApiError &&
                    error._statusCode !== undefined &&
                    error._statusCode >= 500; // Server errors might be temporary
            case 'DEVICE_ERROR':
                return true; // Device might recover
            case 'CONFIGURATION_ERROR':
                return false; // Need to fix configuration
            default:
                return false;
        }
    }

    /**
     * Creates a retry strategy for recoverable errors
     */
    static async withRetry<T>(
        operation: () => Promise<T>,
        context: string,
        maxRetries: number = 3,
        delayMs: number = 1000
    ): Promise<T> {
        let lastError: HikvisionError | undefined;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await ErrorHandler.withErrorHandling(operation, context);
            } catch (error) {
                lastError = error as HikvisionError;

                // Don't retry if error is not recoverable
                if (!ErrorHandler.isRecoverable(lastError)) {
                    throw lastError;
                }

                // Don't retry on last attempt
                if (attempt === maxRetries) {
                    throw lastError;
                }

                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
            }
        }

        // This should never happen since we assign lastError in every iteration
        throw lastError || new Error('Unknown error occurred during retry');
    }
}